import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Search, X, RefreshCw, ChevronLeft, ChevronRight,
  Loader2, FileText, AlertTriangle, ShoppingBag,
  Receipt, DollarSign, CheckCircle, Ban, TrendingUp,
  Printer, Send, Filter, Calendar
} from 'lucide-react';
import Navbar from '../../components/Navbar';
import '../../assets/css/orderhistory.css';
import axios from 'axios';
import { baseurl } from '../../baseurl';

/* ─────────────────────────────────────────────
   API
───────────────────────────────────────────── */
const api = () => {
  const t = localStorage.getItem('restaurant_token');
  return axios.create({ baseURL: baseurl, headers: { Authorization: `Bearer ${t}` } });
};

/* ─────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────── */
const fmtIST = (d) =>
  new Date(d).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });

const fmt  = n => `₹${parseFloat(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const fmtD = n => `₹${parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtN = n => parseInt(n || 0).toLocaleString('en-IN');

const STATUS_META = {
  placed:      { label: 'Placed',    cls: 'placed'      },
  kot:         { label: 'KOT',       cls: 'kot'         },
  preparing:   { label: 'Preparing', cls: 'preparing'   },
  ready:       { label: 'Ready',     cls: 'ready'       },
  served:      { label: 'Served',    cls: 'served'      },
  cancelled:   { label: 'Cancelled', cls: 'cancelled'   },
  placed_print:{ label: 'Printed',   cls: 'placed_print'},
};

const QUICK_DATES = [
  { id: 'today',     label: 'Today',    days: 1  },
  { id: 'week',      label: '7 Days',   days: 7  },
  { id: 'month',     label: '30 Days',  days: 30 },
  { id: 'quarter',   label: '3 Months', days: 90 },
];

/* ─────────────────────────────────────────────
   PRINT BILL  (same professional format)
───────────────────────────────────────────── */
function printBill(order, restaurantProfile = {}) {
  const prof    = typeof restaurantProfile === 'string' ? { name: restaurantProfile } : restaurantProfile;
  const rName   = prof.name || 'Restaurant';
  const tagline = prof.tagline || '';
  const phone   = prof.phone || '';
  const address = [prof.address, prof.city, prof.state, prof.pincode].filter(Boolean).join(', ');
  const gstin   = prof.gst_number || '';
  const footer  = prof.footer_note || 'Thank you for dining with us!';
  const logoUrl = prof.logo_url || '';

  const rawItems = (() => { try { return typeof order.items === 'string' ? JSON.parse(order.items || '[]') : (order.items || []); } catch { return []; } })();
  const dateStr  = fmtIST(order.created_at || new Date());

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"/>
<title>Bill – ${order.order_number}</title>
<style>
  *{margin:0;padding:0;box-sizing:border-box;}
  body{font-family:'Courier New',monospace;font-size:12px;color:#111;width:80mm;margin:0 auto;padding:10px 8px;}
  .hdr{text-align:center;margin-bottom:6px;}
  .logo{max-width:72px;max-height:52px;display:block;margin:0 auto 5px;}
  .rname{font-size:17px;font-weight:900;}
  .rmeta{font-size:10px;color:#555;margin-top:1px;}
  .gstin{font-size:9px;color:#555;font-weight:700;margin-top:2px;}
  .inv-lbl{text-align:center;font-size:10px;font-weight:900;letter-spacing:3px;text-transform:uppercase;margin:4px 0;}
  .div-s{border-top:1px solid #111;margin:5px 0;}
  .div-d{border-top:1px dashed #999;margin:4px 0;}
  .ometa{display:flex;justify-content:space-between;font-size:10px;margin:2px 0;}
  .cust{font-size:11px;margin:4px 0;line-height:1.5;}
  .col-hdr{display:flex;font-size:10px;font-weight:700;color:#555;}
  .col-hdr .cn{flex:1;}.col-hdr .cq{width:26px;text-align:center;}.col-hdr .cr{width:52px;text-align:right;}.col-hdr .ca{width:58px;text-align:right;}
  .irow{display:flex;align-items:flex-start;font-size:11.5px;margin:3px 0;}
  .irow .cn{flex:1;line-height:1.35;}.irow .cq{width:26px;text-align:center;flex-shrink:0;}.irow .cr{width:52px;text-align:right;color:#555;flex-shrink:0;}.irow .ca{width:58px;text-align:right;font-weight:700;flex-shrink:0;}
  .trow{display:flex;justify-content:space-between;font-size:12px;font-weight:600;margin:3px 0;}
  .trow.disc{color:#16a34a;}.trow.grand{font-size:15px;font-weight:900;margin:5px 0 3px;}.trow.pay{font-size:10.5px;color:#555;}
  .ftr{text-align:center;font-size:10px;color:#555;margin-top:8px;line-height:1.55;}
  .pw{font-size:9px;color:#bbb;margin-top:2px;}
  @media print{body{width:100%;}}
</style></head><body>
<div class="hdr">
  ${logoUrl ? `<img src="${logoUrl}" class="logo" alt=""/>` : ''}
  <div class="rname">${rName}</div>
  ${tagline ? `<div class="rmeta">${tagline}</div>` : ''}
  ${address ? `<div class="rmeta">${address}</div>` : ''}
  ${phone   ? `<div class="rmeta">&#128222; ${phone}</div>` : ''}
  ${gstin   ? `<div class="gstin">GSTIN: ${gstin}</div>` : ''}
</div>
<div class="div-s"></div><div class="inv-lbl">Tax Invoice</div><div class="div-s"></div>
<div class="ometa"><span><b>Bill #</b> ${order.order_number}</span><span>${dateStr}</span></div>
${order.table_label ? `<div class="ometa"><span><b>Table:</b> ${order.table_label}</span></div>` : ''}
${(order.customer_name || order.customer_phone) ? `<div class="cust">${order.customer_name ? `<div><b>${order.customer_name}</b></div>` : ''}${order.customer_phone ? `<div>&#128241; ${order.customer_phone}</div>` : ''}</div>` : ''}
<div class="div-d"></div>
<div class="col-hdr"><span class="cn">Item</span><span class="cq">Qty</span><span class="cr">Rate</span><span class="ca">Amt</span></div>
<div class="div-d"></div>
${rawItems.map(i => `<div class="irow"><span class="cn">${i.name}${i.variant === 'half' ? ' (&#189;)' : ''}</span><span class="cq">${i.qty}</span><span class="cr">&#8377;${parseFloat(i.price).toFixed(2)}</span><span class="ca">&#8377;${(parseFloat(i.price) * parseInt(i.qty)).toFixed(2)}</span></div>`).join('')}
<div class="div-d"></div>
<div class="trow"><span>Subtotal</span><span>&#8377;${parseFloat(order.subtotal || 0).toFixed(2)}</span></div>
${parseFloat(order.discount_amount || 0) > 0 ? `<div class="trow disc"><span>Discount</span><span>–&#8377;${parseFloat(order.discount_amount).toFixed(2)}</span></div>` : ''}
<div class="trow"><span>GST (${order.gst_rate || 5}%)</span><span>&#8377;${parseFloat(order.tax_amount || 0).toFixed(2)}</span></div>
${parseFloat(order.delivery_charge || 0) > 0 ? `<div class="trow"><span>Delivery</span><span>&#8377;${parseFloat(order.delivery_charge).toFixed(2)}</span></div>` : ''}
<div class="div-s"></div>
<div class="trow grand"><span>TOTAL</span><span>&#8377;${parseFloat(order.grand_total || 0).toFixed(2)}</span></div>
<div class="trow pay"><span>Payment</span><span>${(order.payment_method || 'cash').toUpperCase()}</span></div>
${order.order_note ? `<div class="rmeta" style="margin-top:3px;font-style:italic;">&#128221; ${order.order_note}</div>` : ''}
<div class="div-d"></div>
<div class="ftr"><div>${footer}</div><div class="pw">Powered by Restrowave POS</div></div>
<script>window.onload=()=>{window.print();window.onafterprint=()=>window.close();}<\/script>
</body></html>`;

  const w = window.open('', '_blank', 'width=360,height=660');
  if (w) { w.document.write(html); w.document.close(); }
}

function sendWhatsApp(order, restaurantName = 'Restaurant') {
  const rawItems = (() => { try { return typeof order.items === 'string' ? JSON.parse(order.items || '[]') : (order.items || []); } catch { return []; } })();
  const lines = rawItems.map(i => `  ${i.name}${i.variant === 'half' ? '(½)' : ''} x${i.qty} = ₹${(parseFloat(i.price) * parseInt(i.qty)).toFixed(2)}`).join('\n');
  const msg = `🧾 *${restaurantName}*\nBill #${order.order_number}\n━━━━━━━━━━━━━━\n${lines}\n━━━━━━━━━━━━━━\nSubtotal: ₹${parseFloat(order.subtotal || 0).toFixed(2)}\nGST: ₹${parseFloat(order.tax_amount || 0).toFixed(2)}\n*Total: ₹${parseFloat(order.grand_total || 0).toFixed(2)}*\nPayment: ${(order.payment_method || 'cash').toUpperCase()}\n\nThank you! 🙏`;
  const phone = (order.customer_phone || '').replace(/\D/g, '');
  window.open(`https://wa.me/${phone ? `91${phone}` : ''}?text=${encodeURIComponent(msg)}`, '_blank');
}

/* ─────────────────────────────────────────────
   ORDER DETAIL DRAWER
───────────────────────────────────────────── */
function OrderDrawer({ order, onClose, restaurantProfile }) {
  const items = useMemo(() => {
    try { return typeof order.items === 'string' ? JSON.parse(order.items || '[]') : (order.items || []); }
    catch { return []; }
  }, [order.items]);

  const sm = STATUS_META[order.status] || STATUS_META.placed;

  return (
    <div className="oh-drawer-overlay" onClick={onClose}>
      <div className="oh-drawer" onClick={e => e.stopPropagation()}>
        <div className="oh-drawer-hd">
          <div className="oh-drawer-title">Order #{order.order_number}</div>
          <button className="oh-drawer-close" onClick={onClose}><X size={14}/></button>
        </div>

        <div className="oh-drawer-body">
          {/* Badges */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <span className={`oh-badge ${sm.cls}`}>{sm.label}</span>
            <span className={`oh-pay ${order.payment_method}`}>
              {order.payment_method?.toUpperCase()}
            </span>
            {order.order_source && (
              <span style={{ fontSize: 10, fontWeight: 700, background: 'var(--bg)', color: 'var(--text-3)', padding: '3px 9px', borderRadius: 100, border: '1px solid var(--border)' }}>
                {order.order_source.toUpperCase()}
              </span>
            )}
          </div>

          {/* Order info */}
          <div>
            <div className="oh-drawer-section">Order Details</div>
            <div className="oh-drawer-grid">
              {[
                ['Order #',   order.order_number],
                ['Type',      order.order_type || '—'],
                ['Table',     order.table_label || '—'],
                ['Customer',  order.customer_name || '—'],
                ['Phone',     order.customer_phone || '—'],
                ['Date',      fmtIST(order.created_at)],
                ['GST',       `${order.gst_rate || 5}% (${order.gst_type || 'exclusive'})`],
                ['Status',    sm.label],
              ].map(([l, v]) => (
                <div key={l} className="oh-drawer-field">
                  <span className="oh-drawer-label">{l}</span>
                  <span className="oh-drawer-val">{v}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Items */}
          {items.length > 0 && (
            <div>
              <div className="oh-drawer-section">Items ({items.length})</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {items.map((it, i) => (
                  <div key={i} className="oh-drawer-item">
                    <div>
                      <div className="oh-drawer-item-name">
                        {it.name}{it.variant === 'half' ? ' ½' : ''} ×{it.qty}
                      </div>
                      {it.note && <div className="oh-drawer-item-meta">{it.note}</div>}
                    </div>
                    <div className="oh-drawer-item-price">{fmtD(it.total || it.price * it.qty)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Totals */}
          <div>
            <div className="oh-drawer-section">Bill Summary</div>
            <div className="oh-drawer-totals">
              <div className="oh-total-row"><span>Subtotal</span><span>{fmtD(order.subtotal)}</span></div>
              {parseFloat(order.discount_amount) > 0 && (
                <div className="oh-total-row" style={{ color: 'var(--mint)' }}>
                  <span>Discount {order.discount_type === 'percent' ? `(${order.discount_value}%)` : ''}</span>
                  <span>−{fmtD(order.discount_amount)}</span>
                </div>
              )}
              <div className="oh-total-row">
                <span>GST ({order.gst_rate || 5}%)</span>
                <span>{fmtD(order.tax_amount)}</span>
              </div>
              {parseFloat(order.delivery_charge) > 0 && (
                <div className="oh-total-row"><span>Delivery</span><span>{fmtD(order.delivery_charge)}</span></div>
              )}
              <div className="oh-total-row grand">
                <span>Grand Total</span>
                <span>{fmtD(order.grand_total)}</span>
              </div>
            </div>
          </div>

          {/* Note */}
          {order.order_note && (
            <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', padding: '10px 14px', fontSize: 12, color: 'var(--text-3)', fontStyle: 'italic' }}>
              📝 {order.order_note}
            </div>
          )}

          {/* Cancellation reason */}
          {order.cancelled_reason && (
            <div className="oh-cancel-reason">
              <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }}/>
              {order.cancelled_reason}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => printBill(order, restaurantProfile)}
              className="oh-btn"
              style={{ flex: 1, justifyContent: 'center' }}>
              <Printer size={13}/> Print Bill
            </button>
            {order.customer_phone && (
              <button
                onClick={() => sendWhatsApp(order, restaurantProfile?.name)}
                style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px', borderRadius: 'var(--r-sm)', border: 'none', background: '#25D366', color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--f-ui)' }}>
                <Send size={13}/> WhatsApp
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
export default function OrderHistory() {
  const [data,          setData]          = useState(null);
  const [loading,       setLoading]       = useState(false);
  const [page,          setPage]          = useState(1);
  const [search,        setSearch]        = useState('');
  const [from,          setFrom]          = useState('');
  const [to,            setTo]            = useState('');
  const [quickDate,     setQuickDate]     = useState('month');
  const [filterStatus,  setFilterStatus]  = useState('');
  const [filterType,    setFilterType]    = useState('');
  const [filterPay,     setFilterPay]     = useState('');
  const [filterSource,  setFilterSource]  = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [restaurantProfile] = useState(() => {
    const name = localStorage.getItem('restaurant_name') || 'Restaurant';
    return { name };
  });

  /* Build query params */
  const buildParams = useCallback((p = 1) => {
    const params = { page: p, limit: 30 };
    if (search)       params.search         = search;
    if (filterStatus) params.status         = filterStatus;
    if (filterType)   params.order_type     = filterType;
    if (filterPay)    params.payment_method = filterPay;
    if (filterSource) params.order_source   = filterSource;

    // Quick date takes priority over manual range
    if (quickDate !== 'custom') {
      const q = QUICK_DATES.find(d => d.id === quickDate);
      if (q) params.days = q.days;
    } else {
      if (from) params.from = from;
      if (to)   params.to   = to;
    }

    return params;
  }, [search, filterStatus, filterType, filterPay, filterSource, quickDate, from, to]);

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const r = await api().get('/orders/history/rest', { params: buildParams(p) });
      setData(r.data);
      setPage(p);
    } catch (_) {}
    finally { setLoading(false); }
  }, [buildParams]);

  // Load on mount + whenever filters change
  useEffect(() => { load(1); }, [filterStatus, filterType, filterPay, filterSource, quickDate]); // eslint-disable-line

  // Search on Enter
  const handleSearchKey = e => { if (e.key === 'Enter') load(1); };

  const summary = data?.summary || {};
  const orders  = data?.orders  || [];
  const pg      = data?.pagination || {};

  return (
    <div className="oh-root">
      <Navbar />
      <div className="oh-page">

        {/* Top bar */}
        <div className="oh-topbar">
          <div>
            <div className="oh-eyebrow">Orders</div>
            <h1 className="oh-title">Order <span>History</span></h1>
            <p className="oh-sub">Complete record of all past orders</p>
          </div>
          <button className="oh-btn" onClick={() => load(page)} disabled={loading}>
            {loading ? <Loader2 size={13} className="oh-spin"/> : <RefreshCw size={13}/>} Refresh
          </button>
        </div>

        {/* Summary strip */}
        {data && (
          <div className="oh-summary-strip">
            {[
              { cls: 's-rev',    icon: <DollarSign size={16}/>,  label: 'Total Revenue',    val: fmt(summary.total_revenue)  },
              { cls: 's-total',  icon: <ShoppingBag size={16}/>, label: 'Total Orders',     val: fmtN(summary.total_orders)  },
              { cls: 's-done',   icon: <CheckCircle size={16}/>, label: 'Completed',        val: fmtN(summary.completed_orders) },
              { cls: 's-cancel', icon: <Ban size={16}/>,         label: 'Cancelled',        val: fmtN(summary.cancelled_orders) },
              { cls: 's-avg',    icon: <TrendingUp size={16}/>,  label: 'Avg Order Value',  val: fmt(summary.avg_order_value) },
            ].map((s, i) => (
              <div key={i} className={`oh-summary-card ${s.cls}`}>
                <div style={{ width: 32, height: 32, borderRadius: 9, background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--tomato)' }}>{s.icon}</div>
                <div className="oh-sc-label">{s.label}</div>
                <div className="oh-sc-val">{s.val}</div>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="oh-filters">
          {/* Search */}
          <div className="oh-search">
            <Search size={13}/>
            <input
              placeholder="Search order#, customer, phone…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={handleSearchKey}
            />
            {search && (
              <button onClick={() => { setSearch(''); load(1); }} style={{ border: 'none', background: 'none', cursor: 'pointer', display: 'flex' }}>
                <X size={11} style={{ color: 'var(--text-4)' }}/>
              </button>
            )}
          </div>

          {/* Quick dates */}
          <div className="oh-quick-dates">
            {QUICK_DATES.map(d => (
              <button key={d.id}
                className={`oh-quick-btn ${quickDate === d.id ? 'active' : ''}`}
                onClick={() => setQuickDate(d.id)}>
                {d.label}
              </button>
            ))}
            <button
              className={`oh-quick-btn ${quickDate === 'custom' ? 'active' : ''}`}
              onClick={() => setQuickDate('custom')}>
              <Calendar size={10}/> Custom
            </button>
          </div>

          {/* Custom date range */}
          {quickDate === 'custom' && (
            <div className="oh-date-range">
              <input type="date" value={from} onChange={e => setFrom(e.target.value)}/>
              <span>→</span>
              <input type="date" value={to} onChange={e => setTo(e.target.value)}/>
              <button className="oh-btn" style={{ padding: '5px 12px', fontSize: 11 }} onClick={() => load(1)}>Go</button>
            </div>
          )}

          {/* Dropdowns */}
          {[
            { val: filterStatus,  set: setFilterStatus,  opts: [['','All Status'],['placed','Placed'],['kot','KOT'],['preparing','Preparing'],['ready','Ready'],['served','Served'],['cancelled','Cancelled']] },
            { val: filterType,    set: setFilterType,    opts: [['','All Types'],['table','Table'],['parcel','Parcel'],['zomato','Zomato'],['swiggy','Swiggy'],['other','Other']] },
            { val: filterPay,     set: setFilterPay,     opts: [['','All Payment'],['cash','Cash'],['card','Card'],['upi','UPI'],['split','Split']] },
            { val: filterSource,  set: setFilterSource,  opts: [['','All Sources'],['pos','POS'],['qr','QR'],['online','Online']] },
          ].map((f, i) => (
            <select key={i} className="oh-select" value={f.val} onChange={e => f.set(e.target.value)}>
              {f.opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          ))}
        </div>

        {/* Table */}
        <div className="oh-table-card">
          {loading && !data && (
            <div className="oh-loading"><Loader2 size={28} className="oh-spin"/><p>Loading orders…</p></div>
          )}

          {!loading && orders.length === 0 && (
            <div className="oh-empty"><Receipt size={36}/><p>No orders found for the selected filters.</p></div>
          )}

          {orders.length > 0 && (
            <>
              <div className="oh-table-wrap">
                <table className="oh-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Customer</th>
                      <th>Type</th>
                      <th>Table</th>
                      <th>Source</th>
                      <th>Status</th>
                      <th>Payment</th>
                      <th>Items</th>
                      <th>Total</th>
                      <th>Date (IST)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map(o => {
                      const rawItems = (() => { try { return typeof o.items === 'string' ? JSON.parse(o.items || '[]') : (o.items || []); } catch { return []; } })();
                      const sm = STATUS_META[o.status] || STATUS_META.placed;
                      return (
                        <tr key={o.id} className="clickable" onClick={() => setSelectedOrder(o)}>
                          <td className="td-num">{o.order_number}</td>
                          <td>
                            <div style={{ fontWeight: 600, color: 'var(--text-1)' }}>{o.customer_name || '—'}</div>
                            {o.customer_phone && (
                              <div style={{ fontSize: 11, color: 'var(--text-4)', fontFamily: 'var(--f-mono)' }}>{o.customer_phone}</div>
                            )}
                          </td>
                          <td className="td-muted" style={{ textTransform: 'capitalize' }}>{o.order_type}</td>
                          <td style={{ fontWeight: 600 }}>{o.table_label || '—'}</td>
                          <td>
                            <span style={{ fontSize: 10, fontWeight: 700, background: 'var(--bg)', color: 'var(--text-3)', padding: '2px 7px', borderRadius: 100, border: '1px solid var(--border)' }}>
                              {o.order_source || 'pos'}
                            </span>
                          </td>
                          <td><span className={`oh-badge ${sm.cls}`}>{sm.label}</span></td>
                          <td><span className={`oh-pay ${o.payment_method}`}>{o.payment_method?.toUpperCase()}</span></td>
                          <td className="td-muted">{rawItems.length} items</td>
                          <td className="td-money">{fmt(o.grand_total)}</td>
                          <td><span className="oh-date">{fmtIST(o.created_at)}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pg.pages > 1 && (
                <div className="oh-pagination">
                  <span className="oh-page-info">
                    Page {pg.page} / {pg.pages} · {fmtN(pg.total)} orders
                  </span>
                  <div className="oh-page-btns">
                    <button className="oh-page-btn" disabled={page <= 1} onClick={() => load(page - 1)}>
                      <ChevronLeft size={14}/>
                    </button>
                    {/* Page numbers — show up to 5 around current */}
                    {Array.from({ length: pg.pages }, (_, i) => i + 1)
                      .filter(n => n === 1 || n === pg.pages || Math.abs(n - page) <= 2)
                      .reduce((acc, n, idx, arr) => {
                        if (idx > 0 && n - arr[idx - 1] > 1) acc.push('…');
                        acc.push(n);
                        return acc;
                      }, [])
                      .map((n, i) =>
                        n === '…'
                          ? <span key={`dots-${i}`} style={{ padding: '0 4px', color: 'var(--text-4)', fontSize: 12 }}>…</span>
                          : (
                            <button key={n} className={`oh-page-btn ${n === page ? 'active' : ''}`}
                              onClick={() => load(n)}>{n}</button>
                          )
                      )
                    }
                    <button className="oh-page-btn" disabled={page >= pg.pages} onClick={() => load(page + 1)}>
                      <ChevronRight size={14}/>
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Order detail drawer */}
      {selectedOrder && (
        <OrderDrawer
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          restaurantProfile={restaurantProfile}
        />
      )}
    </div>
  );
}
